import { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

import { currentSession, saveProperty } from "../../data/accountApi";

/*
 * "Save to my account", beside the other things you can do with a report.
 *
 * WHY IT IS HERE AND NOT ONLY IN THE APP. The Android app has saved
 * properties to the phone since it was built, and the account page has been
 * telling people "saved properties stay on your phone until hosting
 * arrives". Nothing was ever stored against a web account - which also
 * meant the £19.99 subscription was selling "saved historic data" that did
 * not exist.
 *
 * IT CARRIES THE VALUATION WHEN THERE IS ONE, so re-opening a saved
 * property shows the figure that was on screen when it was saved rather
 * than asking for a new one and spending tokens again. A token, not the
 * number - see models/Valuation.js.
 *
 * SIGNED OUT, IT SAYS SO AND OFFERS THE WAY IN rather than hiding. A button
 * that is simply absent reads as a feature that does not exist; this is the
 * moment somebody has a reason to want an account, which is the only moment
 * asking for one is reasonable.
 */

const Wrap = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;

const Button = styled.button`
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: inherit;
    color: #fff;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    transition: all 0.14s ease;
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.14);
        border-color: #fff;
    }
    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
`;

const Note = styled.span`
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
    a {
        color: #fff;
        text-decoration: underline;
    }
`;

export default function SaveProperty({ postcode, cert, valuationToken }) {
    const [session, setSession] = useState(currentSession());
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    /*
     * The session can change while this is on screen - somebody signs in
     * in another tab and comes back. Re-read on focus rather than polling.
     */
    useEffect(() => {
        const refresh = () => setSession(currentSession());
        window.addEventListener("focus", refresh);
        return () => window.removeEventListener("focus", refresh);
    }, []);

    /*
     * A different property is a different save.
     *
     * Hoisted out of the dependency array rather than written inline: the
     * hooks lint cannot statically check an expression there, and the
     * warning is right - an inline `cert && cert.certificateNumber` is the
     * kind of thing that silently stops matching when the shape changes.
     */
    const certNumber = cert ? cert.certificateNumber : null;
    useEffect(() => {
        setDone(false);
        setError(null);
    }, [certNumber]);

    /*
     * A valuation produced AFTER saving re-opens the button.
     *
     * Otherwise the order of two reasonable actions decides whether the
     * valuation is kept: save, then value, and the button says "Saved" and
     * is disabled, with no way to attach the figure that has just appeared.
     * The server upsert already handles the second save; this is the only
     * thing that was stopping it being offered.
     */
    useEffect(() => {
        if (valuationToken) setDone(false);
    }, [valuationToken]);

    if (!cert || !cert.certificateNumber) return null;

    if (!session || !session.token) {
        return (
            <Note>
                <Link to="/account">Sign in</Link> to save this property
            </Note>
        );
    }

    async function save() {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            await saveProperty(session.token, {
                postcode: postcode,
                certificate: cert.certificateNumber,
                address: cert.address || undefined,
                valuation: valuationToken || undefined,
            });
            setDone(true);
        } catch (err) {
            setError((err && err.message) || "Could not save that just now.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Wrap>
            <Button type="button" onClick={save} disabled={busy || done}>
                {done
                    ? "Saved"
                    : busy
                    ? "Saving…"
                    : valuationToken
                    ? "Save with the valuation"
                    : "Save to my account"}
            </Button>
            {done && (
                <Note>
                    <Link to="/account">See your saved properties</Link>
                </Note>
            )}
            {error && <Note>{error}</Note>}
        </Wrap>
    );
}
